<template>
    <big-list-layout
        :slice="sliceData"
        :table-data="getTableData"
    />
</template>

<script>
import { pick, capitalize } from 'lodash';
import BigListLayout from '@/components/BigList/Layout.vue';
export default {
    components: {
        BigListLayout
    },
    props: {
        slice: {
            type: Object,
            required: true
        }
    },
    computed: {
        sliceData() {
            const items = this.slice.items
                .filter(item => this.$validateContentRelationship(item.lender))
                .map(item => {
                    const storeKey = item.lender.type === 'lender_corporate' ? 'corporateLenders' : 'lenders';
                    const modelKey = item.lender.type === 'lender_corporate' ? 'CorporateLoan' : 'Loan';
                    const lender = this.$store.state[storeKey][item.lender.id];
                    const model = new this.$models[modelKey](lender);
                
                    return {
                        ...item, 
                        document: lender,
                        image: lender.data.logo,
                        model: model
                    };
                });
            return {
                ...this.slice,
                items: items
            };
        }, 
        alternateDataConfig() {
            return this.$getAlternateDataConfig(this.slice);
        },
        alternateDataKeys() {
            return this.$getAlternateDataKeys(this.alternateDataConfig);
        }
    },
    methods: {
        getTableData(item) {
            const model = item.model;
            if (item.lender.type === 'lender_corporate') {

                return this.allTableData(
                    [
                        'loanAmountLowest',
                        'loanAmountHighest',
                        'requiresCollateral',
                        'paymentRemarksCompany',
                        'paymentRemarksRepersentative',
                        'takesUCCompany',
                        'takesUCRepersentative',
                    ],
                    model
                );
            }

            if (this.$isSweden()) {
                return this.swedenTableData(model);
            }
            if (this.$isNorway()) {
                return this.norwayTableData(model);
            }
            if (this.$isSpain()) {
                return this.spainTableData(model);
            }
            if (this.$isEstonia()) {
                return this.estonianTableData(model);
            }
            if (this.$isDenmark()) {
                return this.denmarkTableData(model);
            }
            
        },
        swedenTableData(model) {
            if (model.isHighCostCredit) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'creditCheck',
                        'paymentRemarks',
                        'directPayments',
                    ],
                    model
                );
            }
            if (model.isBroker) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'paymentRemarks',
                        'connectedBanks',
                    ],
                    model
                );
            }
            return this.allTableData(
                [
                    'loanAmount',
                    'interest',
                    'duration',
                    'paymentRemarks',
                    'arrangementFee',
                ],
                model
            );
        },
        norwayTableData(model) {
            if (model.isHighCostCredit) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'paymentRemarks',
                        'ageRequirement',
                    ],
                    model
                );
            }
            if (model.isBroker) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'paymentRemarks',
                        'ageRequirement',
                        'connectedBanks',
                    ],
                    model
                );
            }
            return this.allTableData(
                [
                    'loanAmount',
                    'interest',
                    'duration',
                    'paymentRemarks',
                    'ageRequirement',
                    'arrangementFee',
                ],
                model
            );
        },
        spainTableData(model) {
            return this.allTableData(
                [
                    'loanAmount',
                    'duration',
                    'effectiveInterest',
                    'noSalary',
                    'acceptsASNEF',
                    'isBroker',
                    'ageRequirement',
                ],
                model
            );
        },
        estonianTableData(model) {
            return this.allTableData(
                [
                    'loanAmount',
                    'duration',
                    'interest',
                    'effectiveInterest',
                    'arrangementFee',
                    'ageRequirement',
                ],
                model
            );
        },
        denmarkTableData(model) {
            if (model.isHighCostCredit) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'creditCheck',
                        'paymentRemarks',
                        'directPayments',
                    ],
                    model
                );
            }
            if (model.isBroker) {
                return this.allTableData(
                    [
                        'loanAmount',
                        'interest',
                        'duration',
                        'paymentRemarks',
                        'connectedBanks',
                    ],
                    model
                );
            }
            return this.allTableData(
                [
                    'loanAmount',
                    'interest',
                    'duration',
                    'paymentRemarks',
                    'arrangementFee',
                ],
                model
            );
        },
        allTableData(keys, model) {
            const allTableData = {
                loanAmount: {
                    title: this.$translate('loan_amount', 'Belopp'),
                    value: model[this.alternateDataKeys.amountString],
                    showTooltip: this.$validateText(this.$getLoanAmountTooltip(model, this.alternateDataConfig.dataType, this.alternateDataConfig.dataDisplay)),
                    tooltipData: this.$getLoanAmountTooltip(model, this.alternateDataConfig.dataType, this.alternateDataConfig.dataDisplay)
                },
                interest: {
                    title: this.$translate('interest', 'Ränta'),
                    value: model.interestString,
                },
                duration: {
                    title: this.$translate('loan_duration', 'Löptid'),
                    value: this.$getDurationString(model, this.alternateDataConfig.dataType, this.alternateDataConfig.dataDisplay),
                    showTooltip: this.$validateText(this.$getLoanDurationTooltip(model, this.alternateDataConfig.dataType, this.alternateDataConfig.dataDisplay)),
                    tooltipData: this.$getLoanDurationTooltip(model, this.alternateDataConfig.dataType, this.alternateDataConfig.dataDisplay),
                },
                creditCheck: {
                    title: this.$translate('credit_check', 'Kreditupplysning'),
                    value: capitalize(model.creditCheck),
                },
                paymentRemarks: {
                    title: this.$translate('payment_remarks', 'Betalningsanmärkningar'),
                    value: model.acceptsPaymentRemarks 
                        ? this.$translate('accepted', 'Accepteras') 
                        : this.$translate('not_accepted', 'Accepteras inte'),
                },
                directPayments: {
                    title: this.$translate('direct_payout', 'Direktutbetalning'),
                    value: model.hasDirectPayments24Hours 
                        ? this.$translate('twentyfour_seven', 'Dygnet runt') 
                        : this.$formatBoolean(model.hasDirectPayments),
                    showTooltip: this.showDirectPaymentsExplanation(model),
                    tooltipData: this.directPaymentsExplanation(model)
                },
                arrangementFee: {
                    title: this.$translate('arrangement_fee', 'Uppläggningsavgift'),
                    value: this.$getSetupFeeString(model)
                },
                connectedBanks: {
                    title: this.$getConnectedBanksLabel(model),
                    value: model.connectedBanks,
                },
                ageRequirement: {
                    title: this.$translate('min_age', 'Åldersgräns'),
                    value: `${model.minAge} ${this.$translate('years', 'år')}`,
                },
                loanAmountLowest: {
                    title: this.$translate('min_amount', 'Lägsta lånebelopp'),
                    value: model.minAmountString,
                },
                loanAmountHighest: {
                    title: this.$translate('max_amount', 'Högsta lånebelopp'),
                    value: model.maxAmountString,
                },
                requiresCollateral: {
                    title: this.$translate('demands_security', 'Kräver säkerhet'),
                    value: this.$formatBoolean(model.requiresCollateral),
                },
                paymentRemarksCompany: {
                    title: this.$translate('payment_remarks_company', 'Betalningsanmärkningar på företaget'),
                    value: model.acceptsPaymentRemarks?.company 
                        ? this.$translate('accepted', 'Accepteras') 
                        : this.$translate('not_accepted', 'Accepteras inte'),
                },
                paymentRemarksRepersentative: {
                    title: this.$translate('payment_remarks_company_representative', 'Betalningsanmärkningar på firmatecknaren'),
                    value: model.acceptsPaymentRemarks?.repersentative 
                        ? this.$translate('accepted', 'Accepteras') 
                        : this.$translate('not_accepted', 'Accepteras inte'),
                },
                takesUCCompany: {
                    title: this.$translate('takes_uc_on_company', 'Tar uc på företaget'),
                    value: this.$formatBoolean(model.takesUC?.company),
                },
                takesUCRepersentative: {
                    title: this.$translate('takes_uc_on_company_representative', 'Tar uc på firmatecknaren'),
                    value: this.$formatBoolean(model.takesUC?.repersentative),
                },
                effectiveInterest: {
                    title: this.$translate('preamble_effective_interest', 'Effektiv ränta'),
                    value: model.effectiveInterestString
                },
                noSalary: {
                    title: this.$translate('no_salary', 'Utan inkomst från lön'),
                    value: this.$formatBoolean(model.acceptsASNEF),
                },
                acceptsASNEF: {
                    title: this.$translate('with_asnef', 'With ASNEF'),
                    value: this.$formatBoolean(model.acceptsASNEF),
                },
                isBroker: {
                    title: this.$translate('is_broker', 'Är låneförmedlare?'),
                    value: this.$formatBoolean(model.isBroker),
                },

            };
            return Object.values(pick(allTableData, keys));
        },
        showDirectPaymentsExplanation(model) {
            const banks = [
                'Nordea', 'DanskeBank', 'Handelsbanken', 'SEB', 'Swedbank'
            ];

            for (let i = 0; i < banks.length; i++) {
                if (model[`hasDirectPaymentTo${banks[i]}`]) {
                    return true;
                }
            }

            return false;
        },
        directPaymentsExplanation(model) {
            const banks = [
                'Nordea', 'DanskeBank', 'Handelsbanken', 'SEB', 'Swedbank'
            ];

            let hasDirectPaymentTo = [];
            banks.forEach(bank => {
                if (model[`hasDirectPaymentTo${bank}`]) {
                    hasDirectPaymentTo.push(bank);
                }
            });
            return this.$translate('direct_payout_to_bank', 'Direktutbetalning till {bank}')
                .replace('{bank}', hasDirectPaymentTo.join(', '));
        },
    },
};
</script>